/**
 * Feature flags — runtime UI toggles for club program domains.
 *
 * Flags are loaded from the backend at app boot via FeaturesProvider. Admins
 * can flip them in the UI at /admin/feature-flags — no code change or restart
 * needed.
 *
 * Use the `useFeatures()` hook to read flags from any component. The defaults
 * below are only used as a fallback while the initial fetch is in flight or
 * when the backend is unreachable.
 */

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import axiosInstance from './api/axiosConfig';

export type FeatureKey = 'swim' | 'tennis' | 'kidsClub';

export interface FeatureFlag {
  key: string;
  enabled: boolean;
  label: string;
  description: string | null;
  updated_at: string;
}

interface FeaturesContextValue {
  flags: Record<FeatureKey, boolean>;
  list: FeatureFlag[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
  setFlag: (key: FeatureKey | string, enabled: boolean) => Promise<void>;
}

const FALLBACK_FLAGS: Record<FeatureKey, boolean> = {
  swim: true,
  tennis: false,
  kidsClub: false,
};

const FeaturesContext = createContext<FeaturesContextValue>({
  flags: FALLBACK_FLAGS,
  list: [],
  loading: true,
  error: null,
  refresh: async () => {},
  setFlag: async () => {},
});

export const FeaturesProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [list, setList] = useState<FeatureFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await axiosInstance.get<{ data: FeatureFlag[] }>('/feature-flags/');
      setList(res.data.data ?? []);
    } catch (e: unknown) {
      type ErrLike = { response?: { data?: { error?: string } }; message?: string };
      const err = e as ErrLike;
      setError(err?.response?.data?.error || err?.message || 'Failed to load feature flags');
      // keep last-known list; if empty, fallback is applied via reducer below
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const setFlag = useCallback(
    async (key: FeatureKey | string, enabled: boolean) => {
      await axiosInstance.put(`/feature-flags/${key}`, { enabled });
      // Optimistic update — keep UI snappy, then refresh in background
      setList((prev) => prev.map((f) => (f.key === key ? { ...f, enabled } : f)));
      void refresh();
    },
    [refresh],
  );

  const flags = useMemo<Record<FeatureKey, boolean>>(() => {
    const out: Record<FeatureKey, boolean> = { ...FALLBACK_FLAGS };
    for (const f of list) {
      if (f.key === 'swim' || f.key === 'tennis' || f.key === 'kidsClub') {
        out[f.key] = f.enabled;
      }
    }
    return out;
  }, [list]);

  const value = useMemo<FeaturesContextValue>(
    () => ({ flags, list, loading, error, refresh, setFlag }),
    [flags, list, loading, error, refresh, setFlag],
  );

  return <FeaturesContext.Provider value={value}>{children}</FeaturesContext.Provider>;
};

export function useFeatures(): FeaturesContextValue {
  return useContext(FeaturesContext);
}

/** Convenience hook — same return as `useFeatures().flags`. */
export function useFeatureFlags(): Record<FeatureKey, boolean> {
  return useFeatures().flags;
}
